import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Globe, Search, Play, Loader2, CheckCircle2, XCircle, Clock,
  AlertTriangle, Shield, Server, Lock, Unlock, Eye, Trash2,
  RefreshCw, Download, ExternalLink, Network, Info
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const SubdomainFinder = () => {
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [domain, setDomain] = useState('');
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [quickLookupResults, setQuickLookupResults] = useState(null);
  const [activeTab, setActiveTab] = useState('scans');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [scansRes, dashboardRes] = await Promise.all([
        api.get('/domain-intel/scans'),
        api.get('/domain-intel/dashboard')
      ]);
      setScans(scansRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load domain intelligence data');
    } finally {
      setLoading(false);
    }
  };

  const startFullScan = async () => {
    if (!domain.trim()) {
      toast.error('Please enter a domain');
      return;
    }
    
    try {
      setScanning(true);
      const response = await api.post(`/domain-intel/subdomain-scan?domain=${encodeURIComponent(domain.trim())}`);
      toast.success(`Scan started for ${domain}`);
      setDomain('');
      await loadData();
      
      // Poll for completion
      pollScanStatus(response.data.scan_id);
    } catch (error) {
      toast.error('Failed to start scan: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScanning(false);
    }
  };

  const pollScanStatus = async (scanId) => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await api.get(`/domain-intel/scans/${scanId}`);
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          await loadData();
          if (response.data.status === 'completed') {
            toast.success(`Scan completed: Found ${response.data.summary?.total_subdomains || 0} subdomains`);
          } else {
            toast.error('Scan failed: ' + (response.data.error_message || 'Unknown error'));
          }
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        }
      } catch (error) {
        // Ignore polling errors
      }
    };
    
    setTimeout(poll, 3000);
  };

  const quickLookup = async () => {
    if (!domain.trim()) {
      toast.error('Please enter a domain');
      return;
    }
    
    try {
      setScanning(true);
      const response = await api.post(`/domain-intel/quick-lookup?domain=${encodeURIComponent(domain.trim())}`);
      setQuickLookupResults(response.data);
      toast.success(`Found ${response.data.subdomains_found} subdomains`);
    } catch (error) {
      toast.error('Lookup failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScanning(false);
    }
  };

  const viewScanDetails = async (scanId) => {
    try {
      const response = await api.get(`/domain-intel/scans/${scanId}`);
      setSelectedScan(response.data);
    } catch (error) {
      toast.error('Failed to load scan details');
    }
  };

  const deleteScan = async (scanId) => {
    if (!confirm('Delete this scan?')) return;
    
    try {
      await api.delete(`/domain-intel/scans/${scanId}`);
      toast.success('Scan deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete scan');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Network className="w-7 h-7 text-indigo-600" />
            Subdomain Finder
          </h1>
          <p className="text-gray-500">Discover subdomains and identify security risks</p>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">Total Scans</p>
                  <p className="text-3xl font-bold">{dashboard.total_scans || 0}</p>
                </div>
                <Globe className="w-10 h-10 text-indigo-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Subdomains Found</p>
                  <p className="text-3xl font-bold">{dashboard.total_subdomains_discovered || 0}</p>
                </div>
                <Server className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Findings</p>
                  <p className="text-3xl font-bold">
                    {Object.values(dashboard.findings_by_severity || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <AlertTriangle className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">By Severity</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(dashboard.findings_by_severity || {}).map(([sev, count]) => (
                  <Badge key={sev} className={getSeverityColor(sev)}>
                    {sev}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Scan Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Start New Scan
          </CardTitle>
          <CardDescription>
            Enter a domain to discover subdomains and analyze security posture
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                onKeyDown={(e) => e.key === 'Enter' && startFullScan()}
              />
            </div>
            <Button onClick={quickLookup} variant="outline" disabled={scanning}>
              {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Quick Lookup
            </Button>
            <Button onClick={startFullScan} disabled={scanning} className="bg-indigo-600 hover:bg-indigo-700">
              {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Full Scan
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Quick Lookup: Fast check (~20 common subdomains) • Full Scan: Comprehensive enumeration (~100+ subdomains) with security analysis
          </p>
        </CardContent>
      </Card>

      {/* Quick Lookup Results */}
      {quickLookupResults && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Quick Lookup Results: {quickLookupResults.domain}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setQuickLookupResults(null)}>
                Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-3">
              Found {quickLookupResults.subdomains_found} subdomains
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {quickLookupResults.subdomains.map((sub, i) => (
                <div key={i} className="p-2 bg-gray-50 rounded-lg text-sm">
                  <p className="font-medium truncate">{sub.full_domain}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {sub.http_status && (
                      <Badge variant="outline" className="text-xs">HTTP: {sub.http_status}</Badge>
                    )}
                    {sub.https_status && (
                      <Badge variant="outline" className="text-xs text-green-600">HTTPS: {sub.https_status}</Badge>
                    )}
                  </div>
                  {sub.ip_addresses?.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{sub.ip_addresses[0]}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scans Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Scan History</CardTitle>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {scans.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No scans yet</p>
              <p className="text-sm text-gray-400">Start a scan to discover subdomains</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Subdomains</TableHead>
                  <TableHead>Findings</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scans.map((scan) => (
                  <TableRow key={scan.id}>
                    <TableCell className="font-medium">{scan.domain}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(scan.status)}
                        <span className="capitalize">{scan.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>{scan.summary?.total_subdomains || '-'}</TableCell>
                    <TableCell>
                      {scan.summary?.findings_by_severity ? (
                        <div className="flex gap-1">
                          {Object.entries(scan.summary.findings_by_severity).filter(([_, c]) => c > 0).map(([sev, count]) => (
                            <Badge key={sev} className={getSeverityColor(sev)} variant="outline">
                              {count}
                            </Badge>
                          ))}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(scan.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => viewScanDetails(scan.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteScan(scan.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Scan Detail Dialog */}
      <Dialog open={!!selectedScan} onOpenChange={() => setSelectedScan(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedScan && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  {selectedScan.domain}
                </DialogTitle>
                <DialogDescription>
                  Scanned on {new Date(selectedScan.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="subdomains">
                <TabsList className="grid grid-cols-3">
                  <TabsTrigger value="subdomains">
                    Subdomains ({selectedScan.subdomains?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="findings">
                    Findings ({selectedScan.findings?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="dns">DNS Records</TabsTrigger>
                </TabsList>

                <TabsContent value="subdomains" className="mt-4">
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Subdomain</TableHead>
                          <TableHead>IP Address</TableHead>
                          <TableHead>HTTP</TableHead>
                          <TableHead>HTTPS</TableHead>
                          <TableHead>Server</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(selectedScan.subdomains || []).map((sub, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-sm">
                              {sub.full_domain}
                            </TableCell>
                            <TableCell className="text-sm">
                              {sub.ip_addresses?.join(', ') || '-'}
                            </TableCell>
                            <TableCell>
                              {sub.http_status ? (
                                <Badge variant={sub.http_status < 400 ? "outline" : "destructive"}>
                                  {sub.http_status}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {sub.https_status ? (
                                <Badge variant={sub.https_status < 400 ? "outline" : "destructive"} className="text-green-600">
                                  <Lock className="w-3 h-3 mr-1" />
                                  {sub.https_status}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-gray-500 truncate max-w-[150px]">
                              {sub.server || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="findings" className="mt-4">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {(selectedScan.findings || []).map((finding, i) => (
                        <div key={i} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <Badge className={getSeverityColor(finding.severity)}>
                                {finding.severity}
                              </Badge>
                              <h4 className="font-medium">{finding.title}</h4>
                            </div>
                            {finding.cwe_id && (
                              <Badge variant="outline">{finding.cwe_id}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{finding.description}</p>
                          {finding.subdomain && (
                            <p className="text-sm text-gray-400 mt-1">
                              Subdomain: {finding.subdomain}
                            </p>
                          )}
                          {finding.remediation && (
                            <div className="mt-3 p-2 bg-green-50 rounded text-sm text-green-700">
                              <strong>Remediation:</strong> {finding.remediation}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="dns" className="mt-4">
                  <div className="space-y-4">
                    {Object.entries(selectedScan.dns_records || {}).map(([type, records]) => (
                      <div key={type}>
                        <h4 className="font-medium mb-2">{type} Records</h4>
                        {records.length > 0 ? (
                          <div className="bg-gray-50 rounded-lg p-3">
                            {records.map((record, i) => (
                              <code key={i} className="block text-sm text-gray-700">
                                {record}
                              </code>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No records</p>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* SSL Info */}
              {selectedScan.ssl_info && !selectedScan.ssl_info.error && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    SSL Certificate
                  </h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Issuer:</strong> {selectedScan.ssl_info.issuer?.organizationName || 'N/A'}</p>
                    <p><strong>Expires:</strong> {selectedScan.ssl_info.not_after}</p>
                    <p><strong>Subject Alt Names:</strong> {selectedScan.ssl_info.san?.slice(0, 5).join(', ')}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubdomainFinder;
